/**
 * R7 — Curate queue. CD R10 pick Q5: "A Curate queue on §7.23 Operator
 * Workspace, beside Inbox/Alerts/Pins — the curator IS an operator persona,
 * and sign-off becomes an audit event in the trail the Workspace already
 * owns." The Cultural Risk sidebar's "pending review" pointer deep-links here.
 *
 * What this surfaces: the entries in
 * `cultural-risk-factors._pending_human_review.json` — the politically-
 * contested rail (govt-denialism + conflict-sanctions). These are HELD OUT
 * of the live register on purpose: the plugin audit gate (Rule 3) blocks
 * agent-only commits of any `politically-contested` claim. A human curator
 * is the gate. This queue is where that human signs off.
 *
 * ## Honesty rails (load-bearing)
 *
 *   - These are session-local decisions. There is NO persistence layer yet
 *     (Phase 9.4+). Approve/Reject do NOT promote an entry into the live
 *     register or write back to the JSON — they record the curator's
 *     intent for THIS session and emit a `curate.signoff` audit event.
 *   - Sign-off MUST be an audit event. That is the contract: every
 *     Approve/Reject hits `auditCapture.emit` with the entry id as a
 *     subjectId so the §P7.1 Audit trail tab carries the decision.
 *   - The `politically-contested` / `stereotyping-risk` flags are the
 *     REASON each entry sits here — they're rendered prominently, not
 *     buried.
 *   - Per pii-redaction.md the audit payload carries the decision + the
 *     jurisdiction/domain metadata + an optional curator reason; it never
 *     carries the phenomenon prose or citation bodies.
 */

import { useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import type { AuditEventType } from '../audit/types';
import {
  loadPendingCulturalRiskReview,
  type CulturalRiskFactor,
  type CulturalRiskCitation,
  type SensitivityFlag,
} from '@biomax/plugin-region-database';

/** Number of entries awaiting curator review. Exported so the Workspace can
 *  paint the tab count badge without re-deriving the loader call. */
export function pendingReviewCount(): number {
  return loadPendingCulturalRiskReview().length;
}

type Decision = 'approve' | 'reject';

interface SessionDecision {
  decision: Decision;
  reason?: string;
}

/** The two flags that put an entry IN this queue — highlighted prominently. */
const QUEUE_FLAGS: ReadonlySet<SensitivityFlag> = new Set([
  'politically-contested',
  'stereotyping-risk',
]);

const FLAG_LABEL: Record<SensitivityFlag, string> = {
  'politically-contested': 'politically-contested',
  'stereotyping-risk': 'stereotyping-risk',
  'religious-sensitivity': 'religious-sensitivity',
  'stigma-amplification-risk': 'stigma-amplification-risk',
};

export function CurateQueue() {
  const entries = useMemo(() => loadPendingCulturalRiskReview(), []);
  // Session-local only — no persistence layer (Phase 9.4+). Keyed by entry id.
  const [decisions, setDecisions] = useState<Record<string, SessionDecision>>({});

  const decide = (entry: CulturalRiskFactor, decision: Decision, reason?: string) => {
    // LOAD-BEARING: sign-off becomes an audit event in the trail the
    // Workspace already owns. Never log the phenomenon prose / citations.
    // `curate.signoff` is a new taxonomy — Phase 9.4 should add it to the
    // AuditEventType union in audit/types.ts; until then the cast keeps this
    // emit honest about its intended type without widening the closed enum
    // from this surface. The `decision` payload disambiguates it regardless.
    auditCapture.emit({ event: {
      type: 'curate.signoff' as AuditEventType,
      surface: '§7.23',
      subjectIds: [entry.id],
      payload: {
        decision,
        domain: entry.domain,
        iso3: entry.jurisdiction.iso3,
        ...(reason ? { reason } : {}),
      },
    } });
    setDecisions(prev => ({ ...prev, [entry.id]: { decision, ...(reason ? { reason } : {}) } }));
  };

  const reset = (entry: CulturalRiskFactor) => {
    setDecisions(prev => {
      const next = { ...prev };
      delete next[entry.id];
      return next;
    });
  };

  const pending = entries.filter(e => !decisions[e.id]);

  return (
    <div>
      <header style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Curate</h2>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)',
          marginTop: 3,
        }}>
          human curator gate · politically-contested cultural-risk rail
        </div>
      </header>

      {/* Honest framing — WHY these are held, and that decisions are
          session-local until the persistence layer lands. */}
      <div style={{
        padding: '10px 12px', marginBottom: 14,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-threat-3, #d08700)',
        borderRadius: 'var(--bm-radius-sm)',
        fontSize: 12, lineHeight: 1.55, color: 'var(--bm-text)',
      }}>
        These entries are <b>held out of the live register</b> precisely because
        they are politically-contested: the plugin audit gate blocks agent-only
        commits of these claims, so a human curator is the gate. Your{' '}
        <b>Approve / Reject</b> here is a <b>session-local</b> decision and
        becomes an <b>audit event</b> in the §P7.1 trail — it does <i>not</i> yet
        promote an entry into the live register or write back to the source file.
        Durable promotion + write-back land in the persistence layer (Phase 9.4+).
      </div>

      {entries.length === 0 && (
        <div style={{
          padding: 16, textAlign: 'center',
          fontFamily: 'var(--bm-font-mono)', fontSize: 12, color: 'var(--bm-text-subtle)',
        }}>
          Nothing pending review — the politically-contested staging file is empty.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {entries.map(entry => {
          const sessionDecision = decisions[entry.id];
          const queueFlags = entry.sensitivityFlags.filter(f => QUEUE_FLAGS.has(f));
          const otherFlags = entry.sensitivityFlags.filter(f => !QUEUE_FLAGS.has(f));
          return (
            <CurateRow
              key={entry.id}
              entry={entry}
              queueFlags={queueFlags}
              otherFlags={otherFlags}
              decision={sessionDecision}
              onDecide={decide}
              onReset={reset}
            />
          );
        })}
      </div>

      {entries.length > 0 && (
        <div style={{
          marginTop: 14,
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
        }}>
          {pending.length} of {entries.length} awaiting your sign-off · decisions
          reset on reload (session-local)
        </div>
      )}
    </div>
  );
}

interface CurateRowProps {
  entry: CulturalRiskFactor;
  queueFlags: readonly SensitivityFlag[];
  otherFlags: readonly SensitivityFlag[];
  decision?: SessionDecision;
  onDecide: (entry: CulturalRiskFactor, decision: Decision, reason?: string) => void;
  onReset: (entry: CulturalRiskFactor) => void;
}

function CurateRow({ entry, queueFlags, otherFlags, decision, onDecide, onReset }: CurateRowProps) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const decided = Boolean(decision);
  const approved = decision?.decision === 'approve';

  const juris = entry.jurisdiction.subnational
    ? `${entry.jurisdiction.iso3} · ${entry.jurisdiction.subnational}`
    : entry.jurisdiction.iso3;

  return (
    <article
      style={{
        padding: 12,
        background: decided ? 'var(--bm-bg)' : 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: `3px solid ${
          decided
            ? (approved ? 'var(--bm-threat-1, #2e8b57)' : 'var(--bm-text-subtle)')
            : 'var(--bm-threat-3, #d08700)'
        }`,
        borderRadius: 'var(--bm-radius-sm)',
        opacity: decided ? 0.85 : 1,
      }}
    >
      {/* Jurisdiction + domain + evidence row */}
      <header style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 8, marginBottom: 8, flexWrap: 'wrap',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontWeight: 660, fontSize: 13.5 }}>{juris}</span>
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-muted)',
          }}>{entry.domain}</span>
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10,
          padding: '2px 6px', borderRadius: 4,
          border: '1px solid var(--bm-border)', color: 'var(--bm-text-subtle)',
        }}>evidence: {entry.evidenceStrength}</span>
      </header>

      {/* WHY it's here — the queue flags, rendered prominently. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {queueFlags.map(f => (
          <span key={f} style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
            padding: '2px 8px', borderRadius: 999,
            background: 'var(--bm-threat-4, #c1121f)', color: 'var(--bm-bg, #fff)',
            letterSpacing: '.3px',
          }}>⚑ {FLAG_LABEL[f]}</span>
        ))}
        {otherFlags.map(f => (
          <span key={f} style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10,
            padding: '2px 8px', borderRadius: 999,
            border: '1px solid var(--bm-border)', color: 'var(--bm-text-muted)',
          }}>{FLAG_LABEL[f]}</span>
        ))}
        {entry.dataConfidenceModifier !== undefined && (
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10,
            padding: '2px 8px', borderRadius: 999,
            border: '1px solid var(--bm-border)', color: 'var(--bm-text-muted)',
          }}>×{entry.dataConfidenceModifier} undercount</span>
        )}
      </div>

      {/* Phenomenon prose — the claim under review. */}
      <p style={{
        margin: '0 0 8px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text)',
      }}>{entry.phenomenon}</p>

      {/* Citations — publisher + pmid/doi. */}
      <div style={{
        marginBottom: 10, paddingTop: 6, borderTop: '1px dashed var(--bm-border)',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: '.4px', textTransform: 'uppercase', color: 'var(--bm-text-subtle)',
          marginBottom: 4,
        }}>Citations</div>
        <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'disc' }}>
          {entry.exampleCases.map((ex, i) => (
            <li key={i} style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--bm-text-muted)' }}>
              <span style={{ color: 'var(--bm-text)' }}>{ex.citation.publisher}</span>
              {ex.citation.year ? ` (${ex.citation.year})` : ''} · {ex.citation.title}{' '}
              <CitationId citation={ex.citation} />
            </li>
          ))}
        </ul>
      </div>

      {/* Curator actions / decision state. */}
      {!decided && !rejecting && (
        <footer style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => onDecide(entry, 'approve')}
            style={curateBtn('approve')}
          >✓ Approve sign-off</button>
          <button
            type="button"
            onClick={() => setRejecting(true)}
            style={curateBtn('reject')}
          >✕ Reject</button>
        </footer>
      )}

      {!decided && rejecting && (
        <footer style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Optional reason (e.g. phrasing drifts toward stereotyping; source does not support the specific claim)…"
            rows={2}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              fontFamily: 'inherit', fontSize: 12, padding: 8,
              background: 'var(--bm-bg)', color: 'var(--bm-text)',
              border: '1px solid var(--bm-border)', borderRadius: 'var(--bm-radius-sm)',
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => { onDecide(entry, 'reject', reason.trim() || undefined); setRejecting(false); }}
              style={curateBtn('reject')}
            >Confirm reject</button>
            <button
              type="button"
              onClick={() => { setRejecting(false); setReason(''); }}
              style={curateBtn('ghost')}
            >Cancel</button>
          </div>
        </footer>
      )}

      {decided && (
        <footer style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: 'var(--bm-font-mono)', fontSize: 11,
        }}>
          <span style={{ fontWeight: 700, color: approved ? 'var(--bm-threat-1, #2e8b57)' : 'var(--bm-text-muted)' }}>
            {approved ? '✓ Approved (session-local)' : '✕ Rejected (session-local)'}
          </span>
          {decision?.reason && (
            <span style={{ color: 'var(--bm-text-subtle)' }}>· {decision.reason}</span>
          )}
          <button
            type="button"
            onClick={() => onReset(entry)}
            style={{ ...curateBtn('ghost'), marginLeft: 'auto' }}
          >Undo</button>
        </footer>
      )}
    </article>
  );
}

function CitationId({ citation }: { citation: CulturalRiskCitation }) {
  if (citation.pmid) {
    return <code style={{ fontSize: 10.5 }}>PMID:{citation.pmid}</code>;
  }
  if (citation.doi) {
    return <code style={{ fontSize: 10.5 }}>doi:{citation.doi}</code>;
  }
  if (citation.url) {
    return <code style={{ fontSize: 10.5 }}>url</code>;
  }
  return null;
}

function curateBtn(kind: 'approve' | 'reject' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
    fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
    border: '1px solid var(--bm-border)',
  };
  if (kind === 'approve') {
    return { ...base, background: 'var(--bm-threat-1, #2e8b57)', color: 'var(--bm-bg, #fff)', borderColor: 'transparent' };
  }
  if (kind === 'reject') {
    return { ...base, background: 'transparent', color: 'var(--bm-threat-4, #c1121f)', borderColor: 'var(--bm-threat-4, #c1121f)' };
  }
  return { ...base, background: 'transparent', color: 'var(--bm-text-muted)' };
}
